import { useEffect, useId, useRef } from 'react'
import type { JSX, ReactNode } from 'react'

/** Everything inside a panel that can take focus. */
const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'

/**
 * A modal dialog: focus trap, Escape to close, focus restored on the way out.
 *
 * The React equivalent of `Atlas.modal` in design/atlas/components.js — the
 * behaviour S-D1 designed, reimplemented rather than imported because that file
 * drives the static prototypes through the DOM and would fight React for
 * ownership of the same nodes.
 *
 * All three behaviours are the ones a hand-rolled dialog reliably omits, and
 * each has a concrete failure: without the trap, Tab walks into the page behind
 * and the user types into fields they cannot see; without Escape, a dialog on a
 * phone can be unclosable if the footer scrolls off; without restore, the caret
 * lands at the top of the document and a keyboard user starts again.
 *
 * `dismissable` is false for the session-expiry dialog. Everything else can be
 * escaped.
 */
export function Modal({
  title,
  onClose,
  dismissable = true,
  variant = 'panel',
  children,
  footer,
}: {
  title: string
  onClose: () => void
  dismissable?: boolean
  /**
   * `'prompt'` is S-D2 screen 11's dialog: a 24rem card with `radius-xl`,
   * centred, its title a `<strong>` at body size rather than an `<h2>` at
   * 1.5rem in a header row (AUTH-65, AUTH-66, AUTH-68, AUTH-73).
   *
   * A separate shape rather than a restyle of `'panel'`, because the two are
   * for different things. A panel holds a form or a table and is sized so one
   * can be read in it; a prompt asks one question, and at 34rem with a 1.5rem
   * heading it reads as a page that has lost its screen. The dialog this was
   * added for — re-authenticating over the work you were in the middle of —
   * is the case where that matters most: it is meant to look like an
   * interruption you step through, not like the app has restarted.
   *
   * Both keep the focus trap, Escape and focus restoration; only the frame
   * differs.
   */
  variant?: 'panel' | 'prompt'
  children: ReactNode
  footer?: ReactNode
}): JSX.Element {
  const panel = useRef<HTMLDivElement>(null)
  const titleId = useId()

  useEffect(() => {
    const previous = document.activeElement
    const node = panel.current
    node?.querySelector<HTMLElement>(FOCUSABLE)?.focus()

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape' && dismissable) {
        onClose()
        return
      }
      if (event.key !== 'Tab' || !node) return

      const focusable = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)]
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return

      // Wrap in both directions. Reading the list on each Tab rather than once
      // on open, because a dialog's contents change — a validation message adds
      // a link, a pending button becomes disabled — and a stale list traps
      // focus on an element that is no longer there.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (previous instanceof HTMLElement) previous.focus()
    }
  }, [onClose, dismissable])

  return (
    <div
      className="ar-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onMouseDown={(event) => {
        // The backdrop, not the panel. `mousedown` rather than `click` so a
        // drag that starts inside the panel and ends on the backdrop — selecting
        // text to the edge — does not dismiss the dialog.
        if (dismissable && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className={
          variant === 'prompt' ? 'ar-modal__panel ar-modal__panel--prompt' : 'ar-modal__panel'
        }
        ref={panel}
      >
        {/* The prompt carries its title inside the body, so the question and
            the answer are one block. `aria-labelledby` still points at it
            either way — the element moved, the name did not. */}
        {variant === 'prompt' ? null : (
          <div className="ar-modal__header">
            <h2 id={titleId}>{title}</h2>
          </div>
        )}
        {/* `ar-stack` only on the prompt: it owns the whole of its content
            (icon, sentence, field, actions) where a panel's body is handed a
            subtree that already has a layout of its own. */}
        <div className={variant === 'prompt' ? 'ar-modal__body ar-stack' : 'ar-modal__body'}>
          {variant === 'prompt' ? (
            <strong className="app-prompt__title" id={titleId}>
              {title}
            </strong>
          ) : null}
          {children}
        </div>
        {footer ? <div className="ar-modal__footer">{footer}</div> : null}
      </div>
    </div>
  )
}
